// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { isSet } from "utils";
import { IAction } from "actions";
import { IState } from "states/states";
import { IOcppCharger, IRfid } from "states/ocppstates";

import FormattedDate from "./FormattedDate";
import FormattedTime from "./FormattedTime";

interface IRfidDataProps {
  rfid: IRfid | undefined;
  ocppChargers: IOcppCharger[];
  isExtendedDataLoaded: boolean;
}

const mapStateToProps = (state: IState) => {
  const rfid = state.ocpp.rfid.find((curItem) => curItem.id === state.router.rfid);
  const props: IRfidDataProps = {
    rfid,
    ocppChargers: state.ocpp.charger,
    isExtendedDataLoaded: isSet(state.scheduler.ocppRfidListExt.lastData),
  };
  return props;
};

interface IRfidDataDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRfidDataDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  msgUsedEnergy: {
    id: "used energy and costs exclude ongoing charging sessions",
  },
});

class RfidDataComponent extends React.Component<
  IRfidDataProps & IRfidDataDispatch & WrappedComponentProps
> {
  public render() {
    if (!this.props.rfid) {
      return <></>;
    }
    const rfid = this.props.rfid;
    const chargers = (rfid.chargers ?? [])
      .map((curItem) =>
        this.props.ocppChargers.find((curCharger) => curCharger.fk_charger === curItem),
      )
      .map((curItem) => curItem?.name ?? curItem?.chargepoint);

    return (
      <div id="rfiddata">
        <table className="datatable fullwidthwhite">
          <tbody>
            <tr>
              <th>
                <FormattedMessage id="Card number" />
              </th>
              <td>{this.props.rfid?.rfid}</td>
            </tr>
            {this.props.isExtendedDataLoaded && (
              <tr>
                <th>
                  <FormattedMessage id="Chargers" />
                </th>
                <td>
                  {this.props.rfid?.chargerRestricted ? (
                    chargers.join(", ")
                  ) : (
                    <FormattedMessage id="all chargers" />
                  )}
                </td>
              </tr>
            )}
            {isSet(rfid.limitStart) && (
              <tr>
                <th>
                  <FormattedMessage id="Valid from" />
                </th>
                <td>
                  <FormattedDate valueMs={rfid.limitStart * 1000} />
                </td>
              </tr>
            )}
            {isSet(rfid.limitEnd) && (
              <tr>
                <th>
                  <FormattedMessage id="Valid until" />
                </th>
                <td>
                  <FormattedDate valueMs={rfid.limitEnd * 1000} />
                </td>
              </tr>
            )}
            {(isSet(rfid.limitEnergy) || isSet(rfid.limitCost)) && (
              <tr>
                <th
                  title={
                    isSet(rfid.usedEnergy) || isSet(rfid.usedCost)
                      ? this.props.intl.formatMessage(messages.msgUsedEnergy)
                      : undefined
                  }
                >
                  <FormattedMessage id="Energy/Cost limit" />
                </th>
                <td>
                  {isSet(rfid.limitEnergy) &&
                    `${isSet(rfid.usedEnergy) ? `${Math.round(rfid.usedEnergy)} / ` : ""}${
                      rfid.limitEnergy
                    } kWh`}
                  {isSet(rfid.limitEnergy) && isSet(rfid.limitCost) && ", "}
                  {isSet(rfid.limitCost) &&
                    `${isSet(rfid.usedCost) ? `${Math.round(rfid.usedCost)} / ` : ""}${
                      rfid.limitCost
                    } EUR`}
                </td>
              </tr>
            )}
            {this.props.isExtendedDataLoaded && (
              <tr>
                <th>
                  <FormattedMessage id="Last Usage" />
                </th>
                <td>
                  {isSet(rfid.lastUsage) ? (
                    <>
                      <FormattedDate valueMs={rfid.lastUsage * 1000} />{" "}
                      <FormattedTime valueMs={rfid.lastUsage * 1000} />
                    </>
                  ) : (
                    <FormattedMessage id="Never" />
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(RfidDataComponent));
